/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { ReactNode, useState, Fragment } from 'react';
import moment from 'moment';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  Link,
  Paper,
  Typography,
  useTheme,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DoneIcon from '@material-ui/icons/Done';
import { navigate } from 'gatsby';
import colors from '../../../themes/main-colors';
import { CheckBoxes } from '../../pge-plus-common/CheckBoxes';
import useContentMgmt from '../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import { PgeEvModel } from '../../pge-plus-common/PgeEvModel';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import PGEButton from '../../buttons/PGE-Button';
import { Question } from '../../pge-plus-common/Typography';
import { DeclineModal } from './DeclineModal';
import { useEVChargerFormState } from '../../../providers/EVChargerFormStateProvider';
import {
  Quote,
  QuoteData,
  LineItem,
  LineItemCost,
  LineItemDetail,
  LineItemProduct,
  LineItemProgram,
  LineItemRebate,
  SectionEvAccountInfo,
  PreferredContact,
  ApplicationEv,
  ApplicationEvSectionType,
  QuoteDataEv,
} from '../../../__generated__/pgeplus-types';
import useEVApplicationApi from '../../../hooks/pge-plus/useEVApplicationApi';
import ROUTES from '../../../routes';
import useUtil from '../../../hooks/pge-plus/useUtil';
import useWelcomeBackApi from '../../../hooks/pge-plus/useWelcomeBackApi';
import Modal from '../../modal';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import { getPOPUrl } from '../../pge-plus/pay-out-of-pocket/utils/navigation.util';
import PopoverToolTip from '../../pge-plus-common/PopoverToolTip';
import { LINE_ITEM_KEY } from '../../installer-portal/quote.types';
import PGEPlusButtons from '../../pge-plus-common/PGEPlusButton';
import {
  displayPhoneNumberFormat,
  toCurrencyString,
} from '../../../util/format';
import PGEModal from '../../pge-plus-common/Modal';
import Alerts from '../../alerts';
import { useLocation } from '@reach/router';

interface FinalQuoteProps {
  children?: ReactNode;
  path: string;
  viewOnly?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tittle: {
      fontFamily: 'Forma-DJR-Display',
      marginBottom: '30px',
      color: colors.noirBlur,
      letterSpacing: '0.72px',
      textAlign: 'center',
      [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
        textAlign: 'start',
        fontSize: '24px',
      },
    },
    accordianCss: {
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'row',
      paddingBottom: theme.spacing(2),
    },
    fontCss: {
      letterSpacing: '0px',
      fontFamily: 'Untitled-Sans',
      color: colors.noirBlur,
      backgroundColor: colors.lightBlue,
      '& .MuiTypography-body1': {
        fontSize: '20px',
        [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
          fontSize: '16px',
        },
      },
    },
    accordianSumarry: {
      width: '100%',
      variant: 'h6',
      boxShadow: 'none',
      padding: '0px',
      color: colors.noirBlur,
      '& .MuiAccordionSummary-content.Mui-expanded': {
        margin: '0px',
        minHeight: '20px',
      },
      '& .MuiTypography-body1': {
        fontWeight: 700,
        fontSize: '20px',
        lineHeight: '20px',
        [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
          fontSize: '16px',
        },
      },
    },
    submit: {
      backgroundColor: colors.orange,
      borderColor: colors.orange,
      borderRadius: '30px',
      minWidth: '130px',
      '&:hover': {
        backgroundColor: colors.orange,
        borderColor: colors.orange,
      },
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },
    back: {
      minWidth: '130px',
      border: '2px solid #27788C',
      borderRadius: '30px',
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },
    accept: {
      minWidth: '130px',
      border: '2px solid #27788C',
      borderRadius: '30px',
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },
    acceptButton: {
      color: colors.white,
      minWidth: '130px',
      border: '2px solid #27788C',
      background: colors.sparkBlue,
      borderRadius: '30px',
      '&:hover': {
        backgroundColor: colors.buttonHoverBackground,
        borderColor: colors.buttonHoverBackground,
      },
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },

    modalDecline: {
      maxWidth: '150px',
      border: '2px solid #27788C',
      marginRight: '20px',
      borderRadius: '30px',
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },
    close: {
      maxWidth: '150px',
      border: '2px solid #27788C',
      borderRadius: '30px',
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },
    textBold: {
      fontWeight: 'bold',
    },
    subText: {
      fontSize: `${theme.spacing(2)}px !important`,
    },
  }),
);

//TODO: Refactor so this is not needed
// Issue: __typename is not available on LineItem, that's why used below
export type QuoteCustomType = Quote & {
  data: QuoteDataEv & {
    lineItems: [
      | LineItem
      | LineItemCost
      | LineItemDetail
      | LineItemProduct
      | LineItemRebate
      | LineItemProgram,
    ];
  };
};

const LINE_ITEM_KEYS_TO_IGNORE_FOR_INSTALLATION_AMT = [
  LINE_ITEM_KEY.METER_BASE,
  LINE_ITEM_KEY.WEATHER_HEAD,
  LINE_ITEM_KEY.ELECTRICAL_BREAKER,
  LINE_ITEM_KEY.LABOR,
  LINE_ITEM_KEY.MISC_MATERIALS,
  LINE_ITEM_KEY.MATERIALS,
  LINE_ITEM_KEY.ADMIN_FEE,
];

export const FinalQuote: React.FC<FinalQuoteProps> = ({ viewOnly = false }) => {
  const {
    formState: { application },
    updateFormState,
  } = useEVChargerFormState();

  const location = useLocation();
  const locationState = location.state as {
    nextRoute?: string;
  };

  const { acceptQuote, declineQuote } = useEVApplicationApi();
  const { setErrorNotification } = useUtil();

  //TODO: Refactor so this is not needed
  const accountInfo:
    | SectionEvAccountInfo
    | undefined = application?.details?.find(
    detail => detail.sectionType === ApplicationEvSectionType.EvAccountInfo,
  ) as SectionEvAccountInfo;

  //TODO: Refactor so this is not needed
  const prefferedContact: PreferredContact | undefined = accountInfo?.data
    ?.preferredContact as PreferredContact;

  //TODO: Refactor so this is not needed
  const applicationQuote: QuoteCustomType | undefined = application?.job
    ?.quote as QuoteCustomType;

  //TODO: Refactor so this is not needed
  const jobTypeLineItem:
    | LineItemDetail
    | undefined = applicationQuote?.data?.lineItems?.find(
    (lineItem: any) =>
      lineItem.__typename === 'LineItemDetail' &&
      lineItem.key === LINE_ITEM_KEY.JOB_TYPE,
  ) as LineItemDetail;

  const customerVisibleNoteLineItem:
    | LineItemDetail
    | undefined = applicationQuote?.data?.lineItems?.find(
    (lineItem: any) =>
      lineItem.__typename === 'LineItemDetail' &&
      lineItem.key === LINE_ITEM_KEY.CUSTOMER_VISIBLE_NOTES,
  ) as LineItemDetail;

  const [acceptContract, setacceptContract] = useState<string[]>([]);
  const [acceptElectricV, setAcceptElectricV] = useState<string[]>([]);
  const [acceptTnC, setAcceptTnC] = useState<string[]>([]);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [openDeclineModal, setOpenDeclineModal] = useState<string | null>(null);
  const [enrollProgram, setEnrollProgram] = useState<string[]>([]);
  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
  const [isReloadQuoteRequired, setIsReloadQuoteRequired] = useState<boolean>(
    false,
  );
  const { wrapWithLoader } = useWrapWithLoader();
  const { getApplicationByApplicationNo } = useWelcomeBackApi();

  const isEnrollmentDeclined =
    enrollProgram.length > 0 || !!applicationQuote?.data?.declineEnrollment;

  // All below variable will be updated as per api response in wiring story.
  const rebateLineItem: any = applicationQuote?.data?.lineItems?.find(
    (lineItem: any) => lineItem.__typename === 'LineItemRebate',
  );
  const isRebate = Boolean(rebateLineItem);
  const isStandardRebate = rebateLineItem?.key?.includes('S');
  const isPanelUpgrade =
    rebateLineItem?.key === 'SP' || rebateLineItem?.key === 'HP';
  const isHigherRebate = rebateLineItem?.key?.includes('H');
  const isEvPulseTesla = rebateLineItem?.key === 'EVP';

  const isAccepted =
    acceptTnC.length > 0 &&
    acceptElectricV.length > 0 &&
    acceptContract.length > 0;

  const { content, richText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_FINAL_QUOTE,
  );

  const classes = useStyles();
  const theme = useTheme();

  const PRODUCT_DETAILS: any = { amount: 0, name: '' };
  if (applicationQuote?.data?.lineItems) {
    applicationQuote?.data?.lineItems.forEach((lineItem: any) => {
      if (
        lineItem?.__typename === 'LineItemProduct' &&
        lineItem?.key === LINE_ITEM_KEY.EV_CHARGER_MODEL
      ) {
        PRODUCT_DETAILS['name'] = lineItem?.name;
        PRODUCT_DETAILS['amount'] = lineItem?.amount;
      }
    });
  }

  const adminFeeLineItem = applicationQuote?.data?.lineItems?.filter(
    (item: any) =>
      item.__typename === 'LineItemCost' &&
      item.key === LINE_ITEM_KEY.ADMIN_FEE,
  )?.[0] as LineItemCost;

  const onCloseModal = (value: any) => {
    setOpenDeclineModal(value);
  };

  const handleEnrollModal = (value: any) => {
    setEnrollProgram(value);
  };

  const sendEmail = () => {
    setIsEmailSent(true);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [quoteAccepted, setQuoteAccepted] = useState(false);

  const handleAccept = wrapWithLoader(async () => {
    if (applicationQuote) {
      if (application?.financialStanding?.hasPGEPlusDebt) {
        return false;
      }

      try {
        const response = await acceptQuote({
          quoteId: applicationQuote.id,
          version: applicationQuote.version,
          confirmEVVehicleOwner: acceptElectricV.length > 0,
          acknowledgeTerms: acceptTnC.length > 0,
          confirmContractorRebatePayee: acceptContract.length > 0,
          //Send declineEnrollment only when the application has a rebate.
          ...(isRebate
            ? { declineEnrollment: !(enrollProgram.length < 1) }
            : {}),
        });

        if (response?.data) {
          if (applicationQuote.isInstallerToCollectPayment) {
            void navigate(ROUTES.PGE_PLUS_EV_CHARGERS_FINAL_SUCCESS);
          } else {
            setQuoteAccepted(true);
            setIsModalOpen(true);
          }
        } else {
          if (response?.errors?.[0]?.extensions?.code === 'VERSION_MISMATCH') {
            setIsReloadQuoteRequired(true);
          } else {
            setErrorNotification(true);
          }
        }
      } catch (e) {
        setErrorNotification(true);
      }
    } else {
      //TODO: Handle the validation error messages
    }
  });

  const handleNext = () => {
    const route = getPOPUrl(
      ROUTES.PGE_PLUS_PAY_OUT_OF_POCKET_CONFIRM_BILLING_ADDRESS,
      application?.id,
    );

    const state =
      application?.financialStanding?.hasActiveBankruptcy ||
      application?.financialStanding?.hasMissedPGEPlusPayment
        ? { forceFullPayment: true }
        : undefined;

    void navigate(route, { state });
    setIsModalOpen(false);
  };

  const handleDecline = async () => {
    if (applicationQuote) {
      try {
        const response = await declineQuote({
          quoteId: applicationQuote.id,
          version: applicationQuote.version,
        });
        if (response?.data) {
          void navigate(ROUTES.PGE_PLUS_EV_CHARGERS_DECLINE);
        } else {
          //TODO: Handle the validation error messages
        }
      } catch (e) {
        setErrorNotification(true);
      }
    } else {
      //TODO: Handle the validation error messages
    }
  };

  const getDateAndTime = (intervalData: Interval | undefined) => {
    if (intervalData) {
      return {
        date: moment(intervalData.start).format('MMMM D, YYYY'),
        timeRange:
          moment(intervalData.start).format('h A') +
          ' to ' +
          moment(intervalData.end).format('h A'),
      };
    }
    return {};
  };

  const getChargerRebateAmount = () => {
    return isPanelUpgrade
      ? rebateLineItem?.rebateItems?.evCharger
      : rebateLineItem?.amount;
  };

  const getInstallationAmount = () => {
    let installationCost: number = 0;
    applicationQuote?.data?.lineItems?.forEach((lineItem: any) => {
      if (
        lineItem.__typename === 'LineItemCost' &&
        !LINE_ITEM_KEYS_TO_IGNORE_FOR_INSTALLATION_AMT.includes(lineItem.key)
      ) {
        installationCost += lineItem.amount ? lineItem.amount : 0;
      }
    });
    return installationCost;
  };

  const getTotalCostToCustomer = () => {
    const rebateAmount = !isEnrollmentDeclined
      ? rebateLineItem?.amount
        ? rebateLineItem?.amount
        : 0
      : 0;
    const subTotal = PRODUCT_DETAILS.amount + getInstallationAmount();
    const adminFee = adminFeeLineItem?.amount || 0;
    const totalCost = subTotal - rebateAmount;

    return totalCost < 0 ? adminFee : totalCost + adminFee;
  };

  const reloadApplictionData = wrapWithLoader(async () => {
    try {
      const { data } = await getApplicationByApplicationNo({
        applicationId: application?.id!,
      });
      if (data?.getApplication?.application) {
        updateFormState(
          'application',
          data?.getApplication?.application as ApplicationEv,
        );
        setIsReloadQuoteRequired(false);
        setAcceptTnC([]);
        setErrorNotification(
          true,
          content.get('VIEWING_LATEST_QUOTE_SUCCES_BANNER'),
          'success',
        );
      }
    } catch (err) {
      setErrorNotification(true);
    }
  });

  const hasPGEPlusDebt = application?.financialStanding?.hasPGEPlusDebt;

  return (
    <React.Fragment>
      <Box
        css={{
          [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
            marginBottom: '89px',
          },
        }}
      >
        {hasPGEPlusDebt && !viewOnly && (
          <Box
            css={{
              '& .MuiAlert-icon': { color: colors.white, padding: '14px 0' },
            }}
          >
            <Alerts
              isOpen
              severity={'warning'}
              message={
                <Box css={{ color: colors.white, mt: '-1rem' }}>
                  <Typography>
                    {content.get('OUTSTANDING_PGE_PLUS_DEBT_WARNING')}
                  </Typography>
                  <Link
                    onClick={() =>
                      navigate(ROUTES.PGE_PLUS_EV_CHARGERS_WELCOME_BACK)
                    }
                    css={{
                      fontWeight: 700,
                      fontSize: '18px',
                      textDecoration: 'underline',
                      color: colors.white,
                      cursor: 'pointer',
                    }}
                  >
                    {content.get('VIEW_AND_PAY_OUTSTANDING_BALANCE')}
                  </Link>
                </Box>
              }
            />
          </Box>
        )}
        <Box mt={4}>
          <Box>
            <Typography component="h1" variant="h1" className={classes.tittle}>
              {content.get('CUSTOMER_QUOTE_HEADER')}
            </Typography>
          </Box>
          {!viewOnly && (
            <Box mb={3}>
              <Typography
                css={{
                  textAlign: 'left',
                  fontFamily: 'Untitled-Sans',
                  '& .MuiTypography-body1': {
                    fontSize: '20px',
                    [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                      fontSize: '16px',
                    },
                  },
                }}
              >
                {content.get('CUSTOMER_QUOTE_KICKER_TEXT')}
              </Typography>
            </Box>
          )}

          <Box p={2} className={classes.fontCss}>
            <Typography css={{ marginBottom: '10px' }}>
              {content.get('REUSABLE_DATE_COLON')}{' '}
              <strong>{moment().format('MMM. D, YYYY')}</strong>
            </Typography>
            <Typography css={{ marginBottom: '15px' }}>
              {content.get('REUSABLE_REFERENCE_NUMBER_COLON')}{' '}
              <strong>{application?.applicationNo}</strong>
            </Typography>
            {!viewOnly && (
              <Typography>
                <Question
                  detail={richText(content.get('CUSTOMER_QUOTE_PARAGRAPH_1'))}
                />
              </Typography>
            )}
          </Box>
          <Box>
            <Paper
              css={{
                padding: '15px',
                backgroundColor: colors.white,
                letterSpacing: '0px',
                fontFamily: 'Untitled-Sans',
                color: colors.noirBlur,
                '& .MuiTypography-body1': {
                  fontSize: '20px',
                  [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                    fontSize: '16px',
                  },
                },
              }}
            >
              <Accordion
                css={{
                  boxShadow: 'none',
                  '& .MuiAccordionSummary-root.Mui-expanded': {
                    minHeight: '20px',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                  className={classes.accordianSumarry}
                >
                  <Typography>
                    {content.get('CUSTOMER_QUOTE_CUSTOMER_DETAILS')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails css={{ padding: '0px', display: 'block' }}>
                  <Box className={classes.accordianCss}>
                    <Typography>
                      {content.get('REUSABLE_NAME_COLON')}
                    </Typography>
                    <Typography>
                      {(application?.job?.jobLocation?.firstName || '') +
                        ' ' +
                        (application?.job?.jobLocation?.lastName || '')}
                    </Typography>
                  </Box>
                  <Box className={classes.accordianCss}>
                    <Typography>
                      {content.get('REUSABLE_SERVICE_ADDRESS_COLON')}
                    </Typography>
                    <Typography>
                      {application?.job?.jobLocation?.addressLine1}
                    </Typography>
                  </Box>
                  <Box className={classes.accordianCss}>
                    <Typography>
                      {content.get('REUSABLE_PHONE_COLON')}
                    </Typography>
                    <Typography>
                      {displayPhoneNumberFormat(
                        application?.job?.jobLocation?.phone?.replace(
                          /[^0-9]/g,
                          '',
                        ),
                      )}
                    </Typography>
                  </Box>
                  <Box className={classes.accordianCss}>
                    <Typography>
                      {content.get('REUSABLE_EMAIL_ADDRESS_COLON')}
                    </Typography>
                    <Typography>
                      {application?.job?.jobLocation?.email}
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Divider css={{ color: colors.gray93 }} />

              <Accordion
                css={{
                  boxShadow: 'none',
                  '& .MuiAccordionSummary-root.Mui-expanded': {
                    minHeight: '20px',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                  className={classes.accordianSumarry}
                >
                  <Typography>
                    {content.get('CUSTOMER_QUOTE_CONTACT_DETAILS')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails css={{ display: 'block', padding: '0px' }}>
                  <Box className={classes.accordianCss}>
                    <Typography>
                      {content.get('CUSTOMER_QUOTE_MAIN_CONTACT_COLON')}
                    </Typography>
                    <Typography>
                      {prefferedContact?.firstName
                        ? prefferedContact?.firstName +
                          ' ' +
                          (prefferedContact?.lastName || '')
                        : (application?.job?.jobLocation?.firstName || '') +
                          ' ' +
                          (application?.job?.jobLocation?.lastName || '')}
                    </Typography>
                  </Box>
                  <Box className={classes.accordianCss}>
                    <Typography>
                      {content.get('REUSABLE_PHONE_COLON')}
                    </Typography>
                    <Typography>
                      {displayPhoneNumberFormat(
                        prefferedContact?.phoneNumber ||
                          application?.job?.jobLocation?.phone?.replace(
                            /[^0-9]/g,
                            '',
                          ),
                      )}
                    </Typography>
                  </Box>
                  <Box className={classes.accordianCss}>
                    <Typography>
                      {content.get('REUSABLE_EMAIL_ADDRESS_COLON')}
                    </Typography>
                    <Typography>
                      {prefferedContact?.emailAddress ||
                        application?.job?.jobLocation?.email}
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Divider css={{ color: colors.gray93 }} />

              <Accordion
                css={{
                  boxShadow: 'none',
                  '& .MuiAccordionSummary-root.Mui-expanded': {
                    minHeight: '20px',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                  className={classes.accordianSumarry}
                >
                  <Typography>
                    {content.get('CUSTOMER_QUOTE_INSTALLATION_DETAILS')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails css={{ display: 'block', padding: '0px' }}>
                  <Box className={classes.accordianCss}>
                    <Typography>{content.get('REUSABLE_DATE')}</Typography>
                    <Typography className={classes.textBold}>
                      {application?.job?.serviceTime
                        ? getDateAndTime(application?.job?.serviceTime).date
                        : content.get('TO_BE_SCHEDULED')}
                    </Typography>
                  </Box>
                  <Box className={classes.accordianCss}>
                    <Typography>{content.get('REUSABLE_TIME')}</Typography>
                    <Typography className={classes.textBold}>
                      {application?.job?.serviceTime
                        ? getDateAndTime(application?.job?.serviceTime)
                            .timeRange
                        : content.get('TO_BE_SCHEDULED')}
                    </Typography>
                  </Box>
                  <Box className={classes.accordianCss}>
                    <Typography className={classes.subText}>
                      {content.get('INSTALLATION_SCHEDULE_DISCLAIMER')}
                    </Typography>
                  </Box>
                  <Box className={classes.accordianCss}>
                    <Typography>
                      {content.get('CUSTOMER_QUOTE_INSTALLATION_TYPE_COLON')}
                    </Typography>
                    <Typography>
                      {jobTypeLineItem?.value?.split(' ')[0]}
                    </Typography>
                  </Box>
                  <Box className={classes.accordianCss}>
                    <Typography>
                      {content.get(
                        'CUSTOMER_QUOTE_PGE_APPROVED_CONTRACTOR_COLON',
                      )}
                    </Typography>
                    <Typography>
                      {(application?.job?.serviceProviderInfo?.firstName ||
                        '') +
                        (application?.job?.serviceProviderInfo?.lastName || '')}
                    </Typography>
                  </Box>
                  <Box className={classes.accordianCss}>
                    <Typography>
                      {content.get('CUSTOMER_QUOTE_CONTACT_DETAILS')}
                    </Typography>
                    <Typography>
                      {application?.job?.serviceProviderInfo?.phone}
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Divider css={{ color: colors.gray93 }} />

              <Fragment
                css={{
                  letterSpacing: '0px',
                  fontFamily: 'Untitled-Sans',
                  color: colors.noirBlur,
                  '& .MuiTypography-body1': {
                    fontSize: '20px',
                    lineHeight: '20px',
                    [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                      fontSize: '16px',
                    },
                  },
                }}
              >
                <Box mt="10px" mb="15px">
                  <Typography css={{ fontWeight: 700 }}>
                    {content.get('CUSTOMER_QUOTE_COST_DETAILS')}
                  </Typography>
                </Box>
                <Box>
                  <Typography css={{ fontWeight: 700 }}>
                    {content.get('REUSABLE_INSTALLATION')}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>{PRODUCT_DETAILS.name}</Typography>
                  <Typography>
                    $
                    {toCurrencyString(
                      PRODUCT_DETAILS.amount,
                      true,
                      undefined,
                      true,
                    )}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>
                    {content.get('INSTALLATION_LABOR_MATERIALS')}
                  </Typography>
                  <Typography>
                    $
                    {toCurrencyString(
                      getInstallationAmount(),
                      true,
                      undefined,
                      true,
                    )}
                  </Typography>
                </Box>
                {adminFeeLineItem && (
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    mb={1}
                    css={{ '& span': { marginLeft: 0 } }}
                  >
                    <PopoverToolTip
                      popoverContent={content.get(
                        'ADMINISTRATIVE_FEE_TOOLTIP_INFO',
                      )}
                      baseContent={content.get('ADMINISTRATIVE_FEE')}
                    />
                    <Typography>
                      $
                      {toCurrencyString(
                        adminFeeLineItem.amount,
                        true,
                        undefined,
                        true,
                      )}
                    </Typography>
                  </Box>
                )}
                <Divider
                  css={{ color: colors.gray93, margin: '8px 0 8px 0' }}
                />
              </Fragment>

              {isRebate && (
                <Fragment>
                  <Box mb={1} display="flex" justifyContent="space-between">
                    <Typography>
                      {content.get('CUSTOMER_QUOTE_COST_BEFORE_REBATES')}
                    </Typography>
                    <Typography>
                      $
                      {toCurrencyString(
                        PRODUCT_DETAILS.amount +
                          getInstallationAmount() +
                          (adminFeeLineItem?.amount || 0),
                        true,
                        undefined,
                        true,
                      )}
                    </Typography>
                  </Box>

                  <Divider
                    css={{ color: colors.gray93, margin: '8px 0 8px 0' }}
                  />
                </Fragment>
              )}

              {isRebate && (
                <Fragment
                  css={{
                    letterSpacing: '0px',
                    fontFamily: 'Untitled-Sans',
                    color: colors.noirBlur,
                    '& .MuiTypography-body1': {
                      fontSize: '20px',
                      lineHeight: '20px',
                      [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                        fontSize: '16px',
                      },
                    },
                  }}
                >
                  <Box mb={1}>
                    <Typography
                      css={{ fontWeight: 700, color: colors.noirBlur }}
                    >
                      {content.get('REUSABLE_REBATES')}
                    </Typography>
                  </Box>

                  {isStandardRebate && (
                    <Box mb={1} display="flex" justifyContent="space-between">
                      <Typography>
                        {content.get('CUSTOMER_QUOTE_STANDARD_CHARGER_REBATE')}
                      </Typography>
                      {!isEnrollmentDeclined ? (
                        <Typography
                          css={{ fontWeight: 700, color: colors.shoreGreen }}
                        >
                          {getChargerRebateAmount() > 0 && '-'}$
                          {toCurrencyString(
                            getChargerRebateAmount(),
                            true,
                            undefined,
                            true,
                          )}
                        </Typography>
                      ) : (
                        <Typography
                          css={{ fontWeight: 700, color: colors.roseRed }}
                        >
                          -$0.00
                        </Typography>
                      )}
                    </Box>
                  )}

                  {isHigherRebate && (
                    <Box mb={1} display="flex" justifyContent="space-between">
                      <Typography>
                        {content.get('CUSTOMER_QUOTE_HIGHER_CHARGER_REBATE')}
                      </Typography>
                      {!isEnrollmentDeclined ? (
                        <Typography
                          css={{ fontWeight: 700, color: colors.shoreGreen }}
                        >
                          {getChargerRebateAmount() > 0 && '-'}$
                          {toCurrencyString(
                            getChargerRebateAmount(),
                            true,
                            undefined,
                            true,
                          )}
                        </Typography>
                      ) : (
                        <Typography
                          css={{ fontWeight: 700, color: colors.roseRed }}
                        >
                          -$0.00
                        </Typography>
                      )}
                    </Box>
                  )}

                  {isPanelUpgrade && (
                    <Box mb={1} display="flex" justifyContent="space-between">
                      <Typography>
                        {isHigherRebate
                          ? content.get('CUSTOMER_QUOTE_HIGHER_PANEL_REBATE')
                          : content.get('CUSTOMER_QUOTE_STANDARD_PANEL_REBATE')}
                      </Typography>
                      {!isEnrollmentDeclined ? (
                        <Typography
                          css={{ fontWeight: 700, color: colors.shoreGreen }}
                        >
                          {rebateLineItem?.rebateItems?.panelUpgrade > 0 && '-'}
                          $
                          {toCurrencyString(
                            rebateLineItem?.rebateItems?.panelUpgrade,
                            true,
                            undefined,
                            true,
                          )}
                        </Typography>
                      ) : (
                        <Typography
                          css={{ fontWeight: 700, color: colors.roseRed }}
                        >
                          -$0.00
                        </Typography>
                      )}
                    </Box>
                  )}

                  {isEvPulseTesla && (
                    <Box mb={2} display="flex" justifyContent="space-between">
                      <Typography>
                        {content.get('CUSTOMER_QUOTE_EVPULSE_REBATE')}
                      </Typography>
                      {!isEnrollmentDeclined ? (
                        <Typography
                          css={{ fontWeight: 700, color: colors.shoreGreen }}
                        >
                          {rebateLineItem?.amount > 0 && '-'}$
                          {toCurrencyString(
                            rebateLineItem?.amount,
                            true,
                            undefined,
                            true,
                          )}
                        </Typography>
                      ) : (
                        <Typography
                          css={{ fontWeight: 700, color: colors.roseRed }}
                        >
                          -$0.00
                        </Typography>
                      )}
                    </Box>
                  )}

                  {isPanelUpgrade && (
                    <Divider
                      css={{ color: colors.gray93, margin: '8px 0 8px 0' }}
                    />
                  )}
                  {isPanelUpgrade && (
                    <Box mb={2} display="flex" justifyContent="space-between">
                      <Typography>
                        {content.get('CUSTOMER_QUOTE_TOTAL_REBATES')}
                      </Typography>

                      {!isEnrollmentDeclined ? (
                        <Typography
                          css={{ fontWeight: 700, color: colors.shoreGreen }}
                        >
                          {(
                            rebateLineItem?.rebateItems?.evCharger || 0
                          ).toFixed(2) +
                            (rebateLineItem?.rebateItems?.panelUpgrade || 0) >
                            0 && '-'}
                          $
                          {toCurrencyString(
                            (rebateLineItem?.rebateItems?.evCharger || 0) +
                              (rebateLineItem?.rebateItems?.panelUpgrade || 0),
                            true,
                            undefined,
                            true,
                          )}
                        </Typography>
                      ) : (
                        <Typography
                          css={{ fontWeight: 700, color: colors.roseRed }}
                        >
                          -$0.00
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Divider css={{ color: colors.gray93 }} />
                </Fragment>
              )}

              <Fragment
                css={{
                  letterSpacing: '0px',
                  fontFamily: 'Untitled-Sans',
                  color: colors.noirBlur,
                  '& .MuiTypography-body1': {
                    fontSize: '20px',
                    lineHeight: '20px',
                    [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                      fontSize: '16px',
                    },
                  },
                }}
              >
                <Box
                  mt={1}
                  mb={1}
                  display="flex"
                  justifyContent="space-between"
                >
                  <Typography css={{ fontWeight: 700, color: colors.noirBlur }}>
                    {content.get('TOTAL_COST_TO_YOU')}
                  </Typography>
                  <Typography css={{ fontWeight: 700, color: colors.noirBlur }}>
                    $
                    {toCurrencyString(
                      getTotalCostToCustomer(),
                      true,
                      undefined,
                      true,
                    )}
                  </Typography>
                </Box>
                <Divider
                  css={{ color: colors.gray93, margin: '8px 0 8px 0' }}
                />
              </Fragment>

              {isRebate && (
                <Fragment
                  css={{
                    letterSpacing: '0px',
                    fontFamily: 'Untitled-Sans',
                    color: colors.noirBlur,
                    '& .MuiTypography-body1': {
                      fontSize: '20px',
                      lineHeight: '20px',
                      [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                        fontSize: '16px',
                      },
                    },
                  }}
                >
                  <Box mt={2}>
                    <Typography
                      css={{ fontWeight: 700, color: colors.noirBlur }}
                    >
                      {content.get('REUSABLE_PROGRAMS')}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between">
                    <Typography>
                      {content.get('CUSTOMER_QUOTE_SMART_CHARGING_PROGRAM')}
                    </Typography>
                    {!isEnrollmentDeclined ? (
                      <Typography
                        css={{ fontWeight: 700, color: colors.shoreGreen }}
                      >
                        {content.get('CUSTOMER_QUOTE_ENROLLING')}
                      </Typography>
                    ) : (
                      <Typography
                        css={{ fontWeight: 700, color: colors.roseRed }}
                      >
                        {content.get('CUSTOMER_QUOTE_NOT_ENROLLED')}
                      </Typography>
                    )}
                  </Box>
                  {!viewOnly && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography
                        css={{
                          color: colors.sparkBlue,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                        onClick={() => setIsVisible(true)}
                      >
                        {content.get('CUSTOMER_QUOTE_SMART_CHARGING_DETAILS')}
                      </Typography>
                    </Box>
                  )}
                  <Divider
                    css={{ color: colors.gray93, margin: '8px 0 8px 0' }}
                  />
                </Fragment>
              )}

              <Fragment
                css={{
                  letterSpacing: '0px',
                  fontFamily: 'Untitled-Sans',
                  color: colors.noirBlur,
                  '& .MuiTypography-body1': {
                    fontSize: '20px',
                    lineHeight: '20px',
                    [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                      fontSize: '16px',
                    },
                  },
                }}
              >
                <Box mt="10px">
                  <Typography css={{ fontWeight: 700, color: colors.noirBlur }}>
                    {content.get('CUSTOMER_QUOTE_TAX_CREDIT')}
                  </Typography>
                </Box>
                <Box mt="5px" mb="6px">
                  <Typography>
                    <Question
                      detail={content.get('CUSTOMER_QUOTE_TAX_CREDIT_DETAILS')}
                      externalLinkLabel={content.get('REUSABLE_LEARN_MORE')}
                      externalLink={content.get(
                        'CUSTOMER_QUOTE_LEARN_MORE_DETAILS',
                      )}
                    />
                  </Typography>
                </Box>
              </Fragment>
            </Paper>
          </Box>

          <Fragment
            css={{
              letterSpacing: '0px',
              fontFamily: 'Untitled-Sans',
              color: colors.noirBlur,
              backgroundColor: colors.lightBlue,
              '& .MuiTypography-body1': {
                fontSize: '20px',
                [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                  fontSize: '16px',
                },
              },
            }}
          >
            {/* <Box mt={2}>
              <Typography css={{ fontWeight: 700 }}>
                {content.get('CUSTOMER_QUOTE_EMAIL_A_COPY')}
              </Typography>
              <Box css={{ display: 'flex', gridTemplateColumns: '2fr 10fr' }}>
                <TextField
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {isEmailSent && <DoneIcon color="primary" />}
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                  size="small"
                  css={{
                    width: '100%',
                    margin: '8px 0 8px 0',
                    maxWidth: '80%',
                  }}
                  label={'Email'}
                  name="email"
                />

                <Button
                  css={{
                    fontWeight: 700,
                    fontFamily: 'Untitled-Sans',
                    color: colors.sparkBlue,
                  }}
                  onClick={() => sendEmail()}
                >
                  {content.get('REUSABLE_SEND')}
                </Button>
              </Box>
            </Box> */}
          </Fragment>

          {customerVisibleNoteLineItem?.value && (
            <Box mt={4} mb={4}>
              <Paper
                css={{
                  padding: '15px',
                  letterSpacing: '0px',
                  fontFamily: 'Untitled-Sans',
                  color: colors.noirBlur,
                  border: '1px solid #27788c',
                  '& .MuiTypography-body1': {
                    fontSize: '20px',
                    [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                      fontSize: '16px',
                    },
                  },
                }}
              >
                <Fragment
                  css={{
                    letterSpacing: '0px',
                    fontFamily: 'Untitled-Sans',
                    color: colors.noirBlur,
                    '& .MuiTypography-body1': {
                      fontSize: '20px',
                      lineHeight: '20px',
                      [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                        fontSize: '16px',
                      },
                    },
                  }}
                >
                  <Box mt="10px">
                    <Typography
                      css={{ fontWeight: 700, color: colors.noirBlur }}
                    >
                      {content.get('CUSTOMER_QUOTE_ADDITIONAL_NOTES')}
                    </Typography>
                  </Box>
                  <Box mt="5px" mb="6px">
                    <Typography>
                      {customerVisibleNoteLineItem?.value}
                    </Typography>
                  </Box>
                </Fragment>
              </Paper>
            </Box>
          )}

          {!viewOnly && (
            <Fragment>
              <Fragment
                css={{
                  display: 'flex',
                  letterSpacing: '0px',
                  fontFamily: 'Untitled-Sans',
                  color: colors.noirBlur,
                  '& .MuiTypography-body1': {
                    fontSize: '18px !important',
                    lineHeight: '20px',
                  },
                }}
              >
                <Typography
                  css={{
                    fontSize: '16px',
                    color: colors.noirBlur,
                    fontFamily: 'Untitled-Sans',
                    '& span': {
                      color: colors.roseRed,
                    },
                  }}
                >
                  {richText(content.get('CHECK_BOXES_MARKED_WITH_STAR'))}
                </Typography>

                <Typography
                  css={{
                    marginTop: '15px',
                    marginBottom: '30px',
                    '& .MuiTypography-body1': {
                      fontSize: '18px !important',
                      lineHeight: '23px',
                      '& .form.xy-form .xy-form-label-required:after': {
                        color: 'red',
                        content: ' *',
                      },

                      '& .required:after': {
                        content: ' *',
                        color: 'red',
                      },
                    },
                  }}
                >
                  <CheckBoxes
                    options={[
                      {
                        label: content.get(
                          'CUSTOMER_QUOTE_DESIGNATE_PGE_APPROVED_CONTRACTOR',
                        )!,
                        value: content.get(
                          'CUSTOMER_QUOTE_DESIGNATE_PGE_APPROVED_CONTRACTOR',
                        )!,
                        required: '*',
                      },
                    ]}
                    state={acceptContract}
                    setState={setacceptContract}
                  />
                </Typography>

                <Typography
                  css={{
                    '& .PrivateSwitchBase-root': {
                      paddingTop: '0px',
                    },
                    '& .MuiTypography-body1': {
                      fontSize: '18px !important',
                      lineHeight: '23px',
                    },
                  }}
                >
                  <CheckBoxes
                    options={[
                      {
                        label: content.get('CUSTOMER_QUOTE_I_DRIVE_AN_EV')!,
                        value: content.get('CUSTOMER_QUOTE_I_DRIVE_AN_EV')!,
                        required: '*',
                      },
                    ]}
                    state={acceptElectricV}
                    setState={setAcceptElectricV}
                  />
                </Typography>
              </Fragment>

              <Box mt={3}>
                <Typography
                  css={{
                    fontSize: '16px',
                    color: colors.noirBlur,
                    fontFamily: 'Untitled-Sans',
                  }}
                >
                  {content.get('CUSTOMER_QUOTE_PLEASE_REVIEW_TCS')}
                </Typography>
              </Box>

              <Grid
                css={{
                  marginTop: '10px',
                  padding: '10px',
                  backgroundColor: colors.white,
                }}
              >
                <Paper
                  css={{
                    backgroundColor: colors.lightGray2,
                    maxHeight: '386px',
                    textAlign: 'left',
                    overflow: 'auto',
                    boxShadow: 'none',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      backgroundColor: colors.lightGray1,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: colors.sparkBlue,
                      borderRadius: '8px',
                      maxHeight: '20px',
                    },
                  }}
                >
                  <Typography
                    component="p"
                    variant="h3"
                    css={{
                      fontWeight: 700,
                      fontFamily: 'Forma-DJR-Display',
                      marginBottom: '15px',
                    }}
                  >
                    {content.get('REUSABLE_PGE_PLUS_T_N_C')}
                  </Typography>
                  <Typography
                    variant="h6"
                    css={{
                      fontWeight: 400,
                      marginTop: '20px',
                      fontFamily: 'Untitled-Sans',
                      lineHeight: 'normal',
                      marginBottom: '10px',
                    }}
                  >
                    <Link
                      href="https://portlandgeneral.com/PGE+tandc"
                      target="_blank"
                      css={{
                        fontWeight: 700,
                        fontSize: '16px',
                        fontFamily: 'Untitled-Sans',
                        color: colors.sparkBlue,
                        lineHeight: '20px',
                        textDecoration: 'underline',
                      }}
                    >
                      {content.get('REUSABLE_TERMS_AND_CONDITIONS')}
                    </Link>
                  </Typography>
                </Paper>
                <Paper
                  css={{
                    boxShadow: 'none',
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      css={{
                        padding: '10px',
                        '& .MuiTypography-body1': {
                          fontSize: '18px',
                          fontFamily: 'Untitled-Sans',
                          fontWeight: 'normal',
                        },
                        '& .MuiButtonBase-root': {
                          padding: '0 9px',
                        },
                      }}
                    >
                      <CheckBoxes
                        options={[
                          {
                            label: content.get('REUSABLE_I_HAVE_READ_TCS')!,
                            value: content.get('REUSABLE_I_HAVE_READ_TCS')!,
                            required: '*',
                          },
                        ]}
                        state={acceptTnC}
                        setState={setAcceptTnC}
                      />
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Fragment>
                <Box
                  display={'flex'}
                  justifyContent={'center'}
                  flexDirection={'row'}
                  mt="50px"
                  css={{ gap: 32 }}
                >
                  {!quoteAccepted && (
                    <Box>
                      <PGEButton
                        fullWidth
                        type={'button'}
                        variant={'outlined'}
                        color={'secondary'}
                        className={classes.back}
                        onClick={() => setOpenDeclineModal('declineModal')}
                      >
                        {content.get('REUSABLE_BUTTON_DECLINE')}
                      </PGEButton>
                    </Box>
                  )}
                  <DeclineModal
                    path={''}
                    children={openDeclineModal}
                    onCloseModal={onCloseModal}
                    handleDecline={handleDecline}
                  />
                  <Box>
                    {!quoteAccepted && (
                      <PGEButton
                        fullWidth
                        type={'button'}
                        variant={'contained'}
                        className={classes.submit}
                        disabled={!isAccepted || hasPGEPlusDebt}
                        onClick={handleAccept}
                      >
                        {content.get('REUSABLE_BUTTON_ACCEPT')}
                      </PGEButton>
                    )}
                    {quoteAccepted && (
                      <PGEButton
                        fullWidth
                        type={'button'}
                        variant={'contained'}
                        className={classes.submit}
                        disabled={isAccepted ? false : true}
                        onClick={handleNext}
                      >
                        {content.get('REUSABLE_BUTTON_NEXT')}
                      </PGEButton>
                    )}
                  </Box>
                </Box>
              </Fragment>
            </Fragment>
          )}

          {viewOnly && !locationState?.nextRoute && (
            <Box display={'flex'} justifyContent={'center'} mt={4}>
              <PGEPlusButtons
                variant="outlined"
                css={{ minWidth: '10rem' }}
                onClick={() =>
                  navigate(ROUTES.PGE_PLUS_EV_CHARGERS_WELCOME_BACK)
                }
              >
                {content.get('REUSABLE_BUTTON_BACK')}
              </PGEPlusButtons>
            </Box>
          )}
          {viewOnly && locationState?.nextRoute && (
            <Box display={'flex'} justifyContent={'center'} mt={4}>
              <PGEPlusButtons
                variant="contained"
                css={{ minWidth: '10rem' }}
                onClick={() => navigate(locationState?.nextRoute!)}
              >
                {content.get('REUSABLE_BUTTON_NEXT')}
              </PGEPlusButtons>
            </Box>
          )}
        </Box>
        <PgeEvModel
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          handleEnrollModal={handleEnrollModal}
          finalQuote={true}
          finalQuoteData={content}
        />

        <Modal
          open={isReloadQuoteRequired}
          onClose={() => setIsReloadQuoteRequired(false)}
          title={
            <Typography variant="h3">
              {content.get('NEW_QUOTE_AVAILABLE_TITLE')}
            </Typography>
          }
          contentWidth={'500px'}
        >
          <Grid container xs={12} css={{ marginTop: 15 }}>
            <Grid item>
              <Typography>{content.get('NEW_QUOTE_AVAILABLE_DESC')}</Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              css={{
                marginTop: '2em',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Box width={120}>
                <PGEButton
                  className={classes.accept}
                  type={'button'}
                  variant={'contained'}
                  onClick={reloadApplictionData}
                >
                  {content.get('RELOAD_QUOTE')}
                </PGEButton>
              </Box>
            </Grid>
          </Grid>
        </Modal>
      </Box>
      <Fragment>
        <PGEModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={content.get('ACTIVE_BANKRUPTCY_MODAL_TITILE')}
          showCloseIcon={true}
          modalStyles={{ outline: 'none', maxWidth: 560 }}
        >
          <Grid
            css={{
              display: 'flex',
              alignItems: 'flex-end',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="body1"
              css={{ padding: '1.5rem 0rem', fontSize: 20 }}
            >
              {' '}
              {content.get('ACCEPT_QUOTE_MESSAGE')}
            </Typography>
            <Box>
              <PGEButton
                fullWidth
                type={'button'}
                variant={'outlined'}
                color={'secondary'}
                className={classes.acceptButton}
                onClick={handleNext}
              >
                {content.get('CONTINUE')}
              </PGEButton>
            </Box>
          </Grid>
        </PGEModal>
      </Fragment>
    </React.Fragment>
  );
};
